import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { setLastPatchedId, resetLastPatchedId } from 'redux/common/actions';
import {
  fetchCommerceLinksList,
  addCommerceAmount,
  clearAddCommerceAmount,
  deleteCommerceAmount,
  clearDeleteCommerceAmount,
  patchCommerceAmount,
  clearPatchCommerceAmount,
} from 'redux/commerce/actions';

import CommonInputField
  from 'components/elements/inputs/CommonInputField';

import { priceValidation } from 'utils/validations/price';
import { modifyPrice } from 'utils/common';

import './CommerceDonationAmounts.css';

function CommerceDonationAmounts(props) {
  const {
    linkItem,
    setLastPatchedId,
    resetLastPatchedId,
    fetchCommerceLinksList,
    addCommerceAmount,
    clearAddCommerceAmount,
    deleteCommerceAmount,
    clearDeleteCommerceAmount,
    addCommerceAmountSuccess,
    patchCommerceAmountSuccess,
    patchCommerceAmountError,
    deleteCommerceAmountSuccess,
    patchCommerceAmount,
    clearPatchCommerceAmount,
    lastPatchedId,
  } = props;

  useEffect(() => {
    return () => {
      resetLastPatchedId();
    };
  }, [addCommerceAmountSuccess]);

  // Add
  useEffect(() => {
    if (addCommerceAmountSuccess) {
      fetchCommerceLinksList();
      clearAddCommerceAmount();
    }
  }, [addCommerceAmountSuccess]);

  // Patch
  useEffect(() => {
    if (patchCommerceAmountSuccess) {
      fetchCommerceLinksList();
      clearPatchCommerceAmount();
      resetLastPatchedId();
    }
  }, [patchCommerceAmountSuccess]);

  // Delete
  useEffect(() => {
    if (deleteCommerceAmountSuccess) {
      fetchCommerceLinksList();
      clearDeleteCommerceAmount();
    }
  }, [deleteCommerceAmountSuccess]);

  const renderAmountErrors = itemId => {
    if (itemId === lastPatchedId) {
      if (patchCommerceAmountError && patchCommerceAmountError.amount) {
        return patchCommerceAmountError.amount;
      }
      if (
        patchCommerceAmountError &&
        patchCommerceAmountError.non_field_errors &&
        patchCommerceAmountError.non_field_errors.length > 0
      ) {
        return patchCommerceAmountError.non_field_errors[0];
      }
    }
    return null;
  };

  const renderDescriptionErrors = itemId => {
    if (itemId === lastPatchedId) {
      if (patchCommerceAmountError && patchCommerceAmountError.description) {
        return patchCommerceAmountError.description;
      }
    }
    return null;
  };

  const onAddAmount = () => {
    if (linkItem.amount_variants.length < 5) {
      addCommerceAmount({
        commerce_link: linkItem.id,
        amount: Number(linkItem.amount_variants[linkItem.amount_variants.length - 1].amount),
      });
    }
  };

  const renderAmounts = () => (
    linkItem.amount_variants.map((item, i) => (
        <div className="amount-item" key={item.id}>
          <div className="inputs-wrapper">
            {linkItem.payment_provider &&
              <div className="currency-symbol">{item.currency_symbol}</div>
            }
            <CommonInputField
              key={`commerce-donation-amount-input-key-${linkItem.id}-${item.id}`}
              initialValue={modifyPrice(item.amount)}
              executedFunction={value => {
                patchCommerceAmount(item.id, { amount: modifyPrice(value) });
                setLastPatchedId(item.id);
              }}
              serverErrors={renderAmountErrors(item.id)}
              validationFunction={priceValidation}
            />
            {linkItem.link_type === 'payment_form' &&
              <CommonInputField
                key={`commerce-donation-amount-description-input-key-${linkItem.id}-${item.id}`}
                initialValue={item.description}
                executedFunction={value => {
                  patchCommerceAmount(item.id, { description: value });
                  setLastPatchedId(item.id);
                }}
                serverErrors={renderDescriptionErrors(item.id)}
              />
            }
          </div>
          <div className="remove-icon-wrapper">
            {i > 0 &&
              <div
                className="icon"
                onClick={() => deleteCommerceAmount(item.id)}
                role="button"
                tabIndex={-1}
              >
                -
              </div>
            }
          </div>
        </div>
    ))
  );

  return (
    <div className="commerce-donation-amounts__component">
      <div className="title">
        {linkItem.link_type === 'donation' && 'Amount'}
        {linkItem.link_type === 'payment_form' && 'Variants & Options'}
      </div>
      <div className="amounts-wrapper">
        {renderAmounts()}
      </div>
      <div
        className={`add-amount-button${linkItem.amount_variants.length === 5 ? ' disabled' : '' }`}
        onClick={onAddAmount}
        role="button"
        tabIndex={-1}
      >
        <div className="icon">+</div>
        <div className="text">Add another amount (max 5)</div>
      </div>
    </div>
  );
}

CommerceDonationAmounts.propTypes = {
  setLastPatchedId: PropTypes.func.isRequired,
  resetLastPatchedId: PropTypes.func.isRequired,
  fetchCommerceLinksList: PropTypes.func.isRequired,
  addCommerceAmount: PropTypes.func.isRequired,
  clearAddCommerceAmount: PropTypes.func.isRequired,
  deleteCommerceAmount: PropTypes.func.isRequired,
  clearDeleteCommerceAmount: PropTypes.func.isRequired,
  patchCommerceAmount: PropTypes.func.isRequired,
  clearPatchCommerceAmount: PropTypes.func.isRequired,
  linkItem: PropTypes.object.isRequired,
  addCommerceAmountSuccess: PropTypes.object,
  patchCommerceAmountSuccess: PropTypes.object,
  patchCommerceAmountError: PropTypes.object,
  deleteCommerceAmountSuccess: PropTypes.object,
  lastPatchedId: PropTypes.number,
};

CommerceDonationAmounts.defaultProps = {
  addCommerceAmountSuccess: null,
  patchCommerceAmountSuccess: null,
  patchCommerceAmountError: null,
  deleteCommerceAmountSuccess: null,
  lastPatchedId: null,
};

const mapStateToProps = state => (
  {
    addCommerceAmountSuccess: state.commerceReducer.addCommerceAmountSuccess,
    patchCommerceAmountSuccess: state.commerceReducer.patchCommerceAmountSuccess,
    patchCommerceAmountError: state.commerceReducer.patchCommerceAmountError,
    deleteCommerceAmountSuccess: state.commerceReducer.deleteCommerceAmountSuccess,
    lastPatchedId: state.commonReducer.lastPatchedId,
  }
);

export default connect(
  mapStateToProps,
  {
    setLastPatchedId,
    resetLastPatchedId,
    fetchCommerceLinksList,
    addCommerceAmount,
    clearAddCommerceAmount,
    deleteCommerceAmount,
    clearDeleteCommerceAmount,
    patchCommerceAmount,
    clearPatchCommerceAmount,
  },
)(CommerceDonationAmounts);
